import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Typography,
  FormHelperText,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
} from "@mui/material";
import { Alert } from "@mui/lab";
import apiService from "../components/ReasuableFuncations/api";

const regionalOutputs = {
  "Regional Network": [
    {
      output:
        "Developed and enhanced cross-border ICT infrastructure, including terrestrial and submarine fiber optic cables",
      indicators: [
        "Increased cross-border data traffic and reduced latency.",
        "Number of harmonized standards and joint projects.",
      ],
    },
    {
      output:
        "Standardized and reduced costs of international mobile roaming services across regions.",
      indicators: [
        "Reduction in roaming costs across the region.",
        "Number of standardized roaming agreements.",
      ],
    },
    {
      output: "ICT infrastructure shared among service providers",
      indicators: [
        "Establishment of a regulatory framework and increased shared infrastructure use.",
      ],
    },
    {
      output:
        "Regional ICT policies and regulations aligned to facilitate cross-border trade and investment in the ICT sector.",
      indicators: [
        "Number of harmonized policies and reduction in regulatory barriers.",
        "Adoption rate of the regional policy framework across Member States.",
      ],
    },
    {
      output:
        "Regional cybersecurity regulations against cyber threats developed and enforced",
      indicators: [
        "Number of Member States adopting and implementing the regulations.",
        "Frequency of information-sharing sessions and joint cybersecurity initiatives.",
      ],
    },
    {
      output:
        "Legal frameworks governing intellectual property rights to protect innovations developed.",
      indicators: [
        "Number of strengthened and harmonized IPR laws and enforcement cases.",
      ],
    },
    {
      output:
        "Regulatory sandboxes to allow for testing of new technologies and business models before broader deployment implemented.",
      indicators: [
        "Success rate of sandbox projects and integration into mainstream regulations.",
      ],
    },
    {
      output:
        "Sustainable financing models to support ongoing ICT initiatives developed and implemented.",
      indicators: ["Amount of funds raised and invested in ICT initiatives."],
    },
    {
      output:
        "Efficient management of ICT infrastructure and services to reduce operational costs and ensure the sustainability of service provision promoted.",
      indicators: [
        "Number of operational inefficiencies identified and addressed.",
      ],
    },
    {
      output:
        "ICT infrastructure resilient to the impacts of climate change developed.",
      indicators: [
        "Number of operational inefficiencies identified and addressed.",
      ],
    },
  ],
  "Member State": [
    {
      output:
        "A robust Regional ICT infrastructure that supports reliable and widespread connectivity enhanced and maintained",
      indicators: [
        "Increased broadband coverage and penetration rates.",
        "Number of successful partnerships and rollout projects.",
      ],
    },
    {
      output: "ICT infrastructure shared among service providers",
      indicators: ["Increased number of infrastructure-sharing agreements."],
    },
    {
      output:
        "E-payment systems across the region are interoperable, enabling seamless financial transactions and fostering regional trade.",
      indicators: [
        "Number of interoperable systems and increase in cross-border transactions.",
        "Level of integration between systems and user adoption rates.",
      ],
    },
    {
      output:
        "Data protection laws for privacy and security of personal information in the digital economy established and harmonized.",
      indicators: [
        "Number of harmonized data protection laws and compliance levels.",
        "Level of awareness and participation in training programs.",
      ],
    },
    {
      output:
        "Legal frameworks governing intellectual property rights to protect innovations developed.",
      indicators: [
        "Number of trained officials and successful IPR enforcement actions.",
      ],
    },
    {
      output:
        "Regulatory sandboxes to allow for testing of new technologies and business models before broader deployment implemented.",
      indicators: [
        "Number of startups utilizing sandboxes and successful pilot projects.",
      ],
    },
    {
      output: "Digital literacy programs launched and implemented.",
      indicators: [
        "Number of individuals trained and literacy rates improvement.",
        "Integration of ICT in education curricula and student performance.",
      ],
    },
    {
      output:
        "Training programs for ICT professionals developed and implemented.",
      indicators: [
        "Number of ICT professionals trained, and certifications awarded.",
        "Number of partnerships and trainees certified.",
      ],
    },
    {
      output:
        "ICT startups and innovative solutions supported to foster ICT entrepreneurship",
      indicators: [
        "Number of startups incubated and their survival rate.",
        "Number of hackathons held, and innovations developed.",
      ],
    },
    {
      output:
        "Gender responsive rights-based programs for universal access and licensing implemented.",
      indicators: [
        "Number of women and youth participants and their employment rate in ICT.",
        "Number of scholarships awarded and graduation rates.",
      ],
    },
    {
      output:
        "Increased access to ICT infrastructure and services among persons with disabilities.",
      indicators: [
        "Compliance rate with accessibility standards and user satisfaction.",
        "Adoption rates of assistive technologies and accessibility improvements.",
      ],
    },
    {
      output:
        "Environmentally friendly practices within the ICT sector adopted.",
      indicators: [
        "Reduction in energy consumption and carbon footprint.",
        "Number of ICT buildings certified as green.",
      ],
    },
    {
      output:
        "ICT infrastructure resilient to the impacts of climate change developed.",
      indicators: ["Resilience of infrastructure during climate events."],
    },
    {
      output:
        "Programs for the responsible disposal and recycling of electronic waste (e-waste) implemented.",
      indicators: [
        "Volume of e-waste recycled and reduced landfill waste.",
        "Public participation in e-waste recycling programs.",
      ],
    },
    {
      output:
        "Use of renewable energy sources to power ICT infrastructure promoted across the region.",
      indicators: [
        "Percentage of ICT infrastructure powered by renewables.",
        "Number of partnerships and renewable energy projects implemented.",
      ],
    },
    {
      output: "ICT tools utilized for environmental monitoring and management.",
      indicators: [
        "Accuracy and frequency of environmental monitoring reports.",
        "Effectiveness of climate models in predicting events.",
      ],
    },
    {
      output:
        "Public-Private Partnerships to mobilize resources for ICT infrastructure development and service delivery facilitated.",
      indicators: [
        "Number of successful PPP agreements and projects.",
        "Quality and efficiency of ICT services delivered through PPPs.",
      ],
    },
    {
      output:
        "Sustainable financing models to support ongoing ICT initiatives developed and implemented.",
      indicators: ["Revenue generated and reinvested into ICT projects."],
    },
    {
      output:
        "Efficient management of ICT infrastructure and services to reduce operational costs and ensure the sustainability of service provision promoted.",
      indicators: [
        "Reduction in operational costs and improved service reliability.",
      ],
    },
    {
      output:
        "Investment in R&D to drive innovation in the ICT sector promoted.",
      indicators: [
        "Increase in private sector investment in ICT R&D.",
        "Number of R&D centers established, and projects undertaken.",
      ],
    },
    {
      output:
        "Robust monitoring and evaluation frameworks to track the performance and impact of ICT initiatives developed and implemented.",
      indicators: [
        "Timeliness and accuracy of M&E reports.",
        "Percentage of projects meeting their intended outcomes.",
      ],
    },
  ],
};

function ICTStatistics() {
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [outputOptions, setOutputOptions] = useState([]);
  const [indicatorFilter, setIndicatorFilter] = useState("all");
  const [memberRegion, setMemberRegion] = useState(null);

  const fetchProjects = React.useCallback(async () => {
    const userData = JSON.parse(localStorage.getItem("user"));
    if (!userData) {
      console.error("User data not found. Redirecting to login.");
      return;
    }
    const {
      selectedOption,
      regionalNetworkOption,
      memberStateOption,
      countryOption,
    } = userData;
    setMemberRegion(selectedOption);
    const newOutputOptions =
      selectedOption === "Comesa"
        ? [
            ...regionalOutputs["Member State"],
            ...regionalOutputs["Regional Network"],
          ]
        : regionalOutputs[selectedOption];
    setOutputOptions(newOutputOptions);

    let url = "/api/projects-stake";

    try {
      let response;
      if (selectedOption === "Comesa") {
        response = await apiService.get("/api/project-data");
      } else if (
        selectedOption === "Regional Network" &&
        regionalNetworkOption
      ) {
        response = await apiService.get(
          `${url}?regionalNetwork=${regionalNetworkOption}&formType=output`
        );
      } else if (selectedOption === "Member State" && memberStateOption) {
        response = await apiService.get(
          `${url}?country=${countryOption}&formType=output`
        );
      }

      if (response && !response.statusText) {
        throw new Error("Failed to fetch projects");
      }
      setProjects(response.data);
    } catch (error) {
      console.error("Error fetching projects:", error);
      setError("Error fetching projects. Please try again.");
      setSnackOpen(true);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, []);

  const calculatePercentage = (current, target) => {
    if (!current || !target) return "0%";
    const currentValue = parseFloat(current);
    const targetValue = parseFloat(target);
    if (targetValue === 0) return "0%";
    return `${Math.round((currentValue / targetValue) * 100)}%`;
  };

  const validateForm = () => {
    const errors = {};

    // Base validation for project name
    if (!formData.projectName) {
      errors.projectName = "Project Name is required";
    }

    // Validate baseline
    if (!formData.baseline) {
      errors.baseline = `Baseline value is required`;
    }

    // Validate target
    if (!formData.target) {
      errors.target = `Target value is required`;
    }

    // Validate current value
    if (!formData.targetAchieved) {
      errors.current = `Target achieved is required`;
    }

    // Validate indicators
    if (!formData.indicator) {
      errors.indicators = "Indicators are required";
    }

    if (!formData.outputName) {
      errors.outptName = "Output name are required";
    }

    if (!formData.cumulativeTarget) {
      errors.cumulativeTarget = "Cumulative target are required";
    }

    setFormErrors(errors);

    // Log validation results for debugging
    if (Object.keys(errors).length > 0) {
      setError("Please fill in all required fields");
      setSnackOpen(true);
    }

    return Object.keys(errors).length === 0;
  };

  const validateNumberInput = (e) => {
    const value = e.target.value;
    if (!/^\d*\.?\d*$/.test(value)) {
      e.target.value = value.replace(/[^\d.]/g, '');
      return false;
    }
    return true;
  };

  const handleSubmitData = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setError("Please fill in all required fields");
      setSnackOpen(true);
      return;
    }

    try {
      const userData = JSON.parse(localStorage.getItem("user"));
      if (!userData) {
        console.error("User data not found. Redirecting to login.");
        return;
      }
      const {
        selectedOption,
        regionalNetworkOption,
        memberStateOption,
        countryOption,
      } = userData;

      const endpoint = "/api/ict-statistics";

      const response = await apiService.post(endpoint, {
        ...formData,
        selectedOption,
        regionalNetworkOption,
        memberStateOption,
        countryOption,
      });

      if (response.statusText) {
        const successMessages = "Submitted successfully!";
        setSuccess(successMessages);
        setError(null);
        setSnackOpen(true);

        setFormErrors({});
        setFormData({});
      }
    } catch (err) {
      setError("Error processing data. Please try again.");
      setSuccess(null);
      setSnackOpen(true);
      console.error("Error:", err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Clear error for the field being changed
    setFormErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));

    // Add number validation for numeric fields
    const numericFields = ['baseline', 'target', 'targetAchieved', 'cumulativeTarget'];
    if (numericFields.includes(name)) {
      if (!validateNumberInput(e)) {
        return;
      }
    }
    
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const getOutputIndicators = () =>
    outputOptions.find((item) => item.output === formData?.outputName)
      ?.indicators || [];

  return (
    <Container maxWidth="md">
      <Paper elevation={3} style={{ padding: "16px" }}>
        <Typography variant="h5" gutterBottom>
          ICT Statistics Form
        </Typography>
        <form onSubmit={handleSubmitData}>
          <FormControl fullWidth margin="normal">
            <InputLabel>Project Name*</InputLabel>
            <Select
              name="projectName"
              label="Project Name"
              value={formData.projectName || ""}
              onChange={handleInputChange}
              required
            >
              {projects.map((project) => (
                <MenuItem key={project._id} value={project._id}>
                  {project.projectName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {memberRegion && memberRegion === "Comesa" && (
            <FormControl style={{ width: "100%" }}>
              <FormLabel id="demo-radio-buttons-group-label">
                Regional Output Indicators Filter
              </FormLabel>
              <RadioGroup
                aria-labelledby="demo-radio-buttons-group-label"
                defaultValue={indicatorFilter}
                onChange={(e) => setIndicatorFilter(e.target.value)}
                style={{ display: "flex", flexDirection: "row" }}
              >
                <FormControlLabel value="all" control={<Radio />} label="All" />
                <FormControlLabel
                  value="Member State"
                  control={<Radio />}
                  label="Member State"
                />
                <FormControlLabel
                  value="Regional Network"
                  control={<Radio />}
                  label="Regional Network"
                />
              </RadioGroup>
            </FormControl>
          )}

          <FormControl fullWidth margin="normal" error={!!formErrors.outptName}>
            <InputLabel id="output-label">Output Name*</InputLabel>
            <Select
              labelId="output-label"
              name="outputName"
              value={formData.outputName}
              onChange={handleInputChange}
              variant="outlined"
              label="Output Name"
              required
            >
              {indicatorFilter === "all"
                ? outputOptions.map((item, index) => (
                    <MenuItem key={index} value={item.output}>
                      {item.output}
                    </MenuItem>
                  ))
                : [...regionalOutputs[indicatorFilter]].map((item, index) => (
                    <MenuItem key={index} value={item.output}>
                      {item.output}
                    </MenuItem>
                  ))}
            </Select>
            {formErrors.outputName && (
              <FormHelperText>{formErrors.outputName}</FormHelperText>
            )}
          </FormControl>

          <FormControl
            fullWidth
            margin="normal"
            error={!!formErrors.indicators}
          >
            <InputLabel id="indicators-label">Output Indicators*</InputLabel>
            <Select
              labelId="indicators-label"
              name="indicator"
              value={formData.indicator}
              onChange={handleInputChange}
              variant="outlined"
              label="Output Indicator"
              required
            >
              {getOutputIndicators().map((indicator, index) => (
                <MenuItem key={index} value={indicator}>
                  {indicator}
                </MenuItem>
              ))}
            </Select>
            {formErrors.indicators && (
              <FormHelperText>{formErrors.indicators}</FormHelperText>
            )}
          </FormControl>

          <TextField
            fullWidth
            margin="normal"
            name="baseline"
            label="Baseline Value"
            variant="outlined"
            type="text"
            value={formData.baseline || ""}
            onChange={handleInputChange}
            required
            onKeyPress={(e) => {
              if (!/[0-9.]/.test(e.key)) {
                e.preventDefault();
              }
            }}
            helperText={formErrors.baseline || "Enter numbers only"}
          />
          <TextField
            fullWidth
            margin="normal"
            name="target"
            label="Target Value"
            variant="outlined"
            type="text"
            value={formData.target || ""}
            onChange={handleInputChange}
            required
            onKeyPress={(e) => {
              if (!/[0-9.]/.test(e.key)) {
                e.preventDefault();
              }
            }}
            helperText={formErrors.target || "Enter numbers only"}
          />
          <TextField
            fullWidth
            margin="normal"
            name="targetAchieved"
            label="Target Achieved Value"
            variant="outlined"
            type="text"
            value={formData.targetAchieved || ""}
            onChange={handleInputChange}
            required
            onKeyPress={(e) => {
              if (!/[0-9.]/.test(e.key)) {
                e.preventDefault();
              }
            }}
            helperText={formErrors.targetAchieved || "Enter numbers only"}
          />

          <TextField
            fullWidth
            margin="normal"
            name="cumulativeTarget"
            label="Cumulative Target Value"
            variant="outlined"
            type="text"
            onChange={handleInputChange}
            value={formData.cumulativeTarget || ""}
            onKeyPress={(e) => {
              if (!/[0-9.]/.test(e.key)) {
                e.preventDefault();
              }
            }}
            helperText={formErrors.cumulativeTarget || "Enter numbers only"}
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 3,
            }}
          >
            <Button type="submit" variant="contained" color="primary">
              SAVE & SUBMIT
            </Button>
          </Box>
        </form>
      </Paper>
      <Snackbar
        open={snackOpen}
        autoHideDuration={6000}
        onClose={() => setSnackOpen(false)}
      >
        <Alert
          onClose={() => setSnackOpen(false)}
          severity={success ? "success" : "error"}
        >
          {success || error}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default ICTStatistics;
